import {
    any,
    compose,
    equals,
    filter,
    find,
    flatten,
    includes,
    lensPath,
    lensProp,
    map,
    pathOr,
    prop,
    propEq,
    set,
    toLower,
    trim,
    uniq,
    uniqBy,
    when
} from "ramda";
import {
    RolePermission,
    PermissionTemplate,
    FeaturePermissionTemplates
} from "src/types/Permissions";
import { UserRole } from "src/types/Role";
import { filterIsNotEmpty, isNotEmpty, returnIsNotEmptyOrTrue } from "src/utilities/hasValue";
import { getNamespaceName } from "src/services/objects/namespaces";



export const getVarMapNames = (name: string, namespace: string = getNamespaceName()) => {
    return [`${namespace}_${name}`, name]
}


/*
PermissionTemplateClass is to handle the data of global_permission_template 
which is global permission across all namespaces
*/
class PermissionTemplateClass {
    permission: PermissionTemplate;
    namespace: string;
    constructor(permission: PermissionTemplate) {
        this.permission = permission
        this.namespace = getNamespaceName()

    }

    description() {
        return pathOr('', ['description'], this.permission)
    }

    name() {
        return pathOr('', ['permission'], this.permission)
    }

    title() {
        return pathOr('', ['title'], this.permission)
    }

    category() {
        return pathOr('', ['category'], this.permission)
    }

    options() {
        return pathOr({}, ['options'], this.permission)
    }

}

/*
RolePermissionClass is to handle the data of role_permission
which is allocated to specific account role
*/
class RolePermissionClass {
    permission?: RolePermission;
    constructor(permission?: RolePermission) {
        this.permission = permission
    }

    uid() {
        return pathOr('', ['id'], this.permission)
    }

    name() {
        return pathOr('', ['name'], this.permission)
    }

    options() {
        return pathOr({}, ['options'], this.permission)
    }

    multiOption() {
        return pathOr([], ['options', 'multi_option'], this.permission)
    }

    loanStatus() {
        return pathOr([], ['options', 'loan_status'], this.permission)
    }


    setMultiOption(options: any[]) {
        this.permission = set(
            lensPath(['options', 'multi_option']), options, this.permission
        )
    }

    setLoanStatus(options: any[]) {
        this.permission = set(
            lensPath(['options', 'loan_status']), options, this.permission
        )
    }

    eventContext() {
        const eventContext = pathOr([], ['options', 'event_context'], this.permission)
        return eventContext
    }
}

/*
Permission is to manage data of both global_permission_template and role_permission
which is manage the answer (selected/options) role_permission 
other standard flags from global_permission_template
also make payload of role_permission to create or update
*/
class Permission {
    permission: RolePermissionClass;
    template: PermissionTemplateClass;
    selected: boolean = false;
    namespace: string;

    constructor(
        template: PermissionTemplate,
        permission?: RolePermission
    ) {
        this.template = new PermissionTemplateClass(template);
        this.permission = new RolePermissionClass(permission);
        this.selected = isNotEmpty(this.permission.name());
        this.namespace = getNamespaceName()
    }

    idKey() {
        return this.name()
    }

    description() {
        return this.template.description()
    }

    title() {
        return this.template.title()
    }

    category() {
        return this.template.category()
    }

    name() {
        return this.template.name()
    }

    inputOptions() {
        return this.template.options()
    }

    isSelected() {
        return this.selected
    }

    isTurnedOn() {
        return isNotEmpty(this.permission.name())
    }

    setChecked(checked: boolean) {
        this.selected = checked
    }

    setMultiOption(options: any) {
        this.permission.setMultiOption(options)
    }

    setLoanStatus(options: any) {
        this.permission.setLoanStatus(options)
    }

    options() {
        return this.permission.options()
    }

    multiOptions() {
        return this.permission.multiOption()
    }
    loanStatus() {
        return this.permission.loanStatus()
    }

    uid() {
        return this.permission.uid()
    }


    // eventContext/Options/loanStatus VarMap get the event_context's varmap config names if there is no configured 
    // returns standard name <namespace>_<permission_name> and <permission_name>
    getVarMapNames() {
        const varmapName = pathOr('', ['varmap'], this.template.options())
        return returnIsNotEmptyOrTrue(
            varmapName,
            [varmapName],
            getVarMapNames(this.name(), this.namespace)
        )
    }


    getLoanStatus() {
        return this.permission.multiOption()
    }

    eventContext() {
        return pathOr([], ['event_context'], this.permission.options())
    }

    config() {
        const options = this.options()
        return {
            eventContext: this.eventContext(),
            multiOptions: this.multiOptions(),
            ownerSelection: pathOr('', ['owner_selection'], options),
            loanPermission: pathOr('', ['loan_permission'], options),
            mandatoryFields: pathOr('', ['mandatory_fields'], options),
            loanStatus: pathOr({}, ['loan_status'], options)
        }
    }

    payload(roleId: string) {
        const eventContext = this.eventContext()
        const multiOptions = returnIsNotEmptyOrTrue(this.multiOptions(), this.multiOptions(), {})
        const permission = {
            name: this.name(),
            role_id: roleId,
            description: this.description(),
            metadata: {},
            options: compose(
                when(
                    () => isNotEmpty(this.category()) && isNotEmpty(multiOptions),
                    set(lensPath(['multi_option']), multiOptions)
                ),
                when(
                    () => isNotEmpty(eventContext),
                    set(lensPath(['event_context']), eventContext)
                )
            )(this.permission.options()),
            updated_at: 'now()'
        } as RolePermission

        if (this.uid()) {
            return set(lensProp('id'), this.uid(), permission)
        }
        return permission
    }
}

/*
FeaturePermissionsGroup is used to manage a feature set of permissions
*/
class FeaturePermissionsGroup {
    feature: FeaturePermissionTemplates;
    globalPermissions: Permission[]
    permissions: Permission[];

    constructor(feature: FeaturePermissionTemplates, globalPermissions: Permission[]) {
        this.feature = feature
        this.globalPermissions = globalPermissions
        this.permissions = filter(isNotEmpty, map((name: string) => {
            const permission = find((p: Permission) => equals(p.name(), name), this.globalPermissions)
            return permission
        }, this.permissionTemplates()))
    }

    name() {
        return pathOr('', ['name'], this.feature)
    }

    title() {
        return pathOr('', ['title'], this.feature)
    }

    description() {
        return pathOr('', ['description'], this.feature)
    }

    permissionTemplates() {
        return pathOr([], ['permission_templates'], this.feature)
    }

    featurePermissions() {
        return this.permissions
    }

    hasKey(searchKey: string) {
        if (!isNotEmpty(trim(searchKey)) || includes(toLower(trim(searchKey)), toLower(this.title()))) {
            return true
        }
        // Based on search value priority arrage the order values of permission
        const fkeys = (p: Permission) => filterIsNotEmpty(
            [
                p.name(),
                p.title(),
                p.category()
            ]
        )

        return any(equals(true), map((p: Permission) => {
            const matches = map(
                (vm: any) => includes(toLower(trim(searchKey)), toLower(vm)),
                fkeys(p)
            )
            return any(equals(true), matches)
        }, this.permissions))
    }
}


/*
ManageRolePermissions is used to manage a role's permissions based on products configured
fetch all product's unique feature permission groups
by default enable all 'basic' category features for all products across all clients
*/
class ManageRolePermissions {

    userPermissions: RolePermission[]
    loanProducts: any;
    globalFeatures: any;
    globalPermissions: Permission[];
    role: UserRole;
    searchKey: string;

    // initialize class with global features and permissions 
    constructor(
        permissionTemplates: PermissionTemplate[],
        globalFeatures: any,
        role: UserRole,
        loanProducts: any
    ) {
        const userPermissions: any = pathOr([], ['permissions'], role);
        this.role = role
        this.userPermissions = userPermissions
        this.loanProducts = loanProducts
        this.globalFeatures = globalFeatures
        this.globalPermissions = map((template: PermissionTemplate) => {
            const permission = pathOr('', ['permission'], template)
            return new Permission(
                template,
                find(propEq('name', permission), userPermissions) as any,
            )
        }, permissionTemplates)
        this.searchKey = ''
    }

    roleId() {
        return pathOr('', ['id'], this.role)
    }

    name() {
        return pathOr('', ['name'], this.role)
    }

    description() {
        return pathOr('', ['description'], this.role)
    }

    // filter configred feature names on current namespace's products
    productEnabledFeatures(permissionGroups: string[]) {
        // filter all features configured in loan_products
        const featureNames = isNotEmpty(permissionGroups) ? permissionGroups : uniq(flatten([map(pathOr([], ['services', 'features']), this.loanProducts)]))

        // filter features which available in globalFeatures which is Supported features by Lendsmart
        return filter(isNotEmpty, map((name: string) => {
            return find(propEq('name', name), this.globalFeatures)
        }, featureNames))
    }

    // Merge all feautres as FeaturePermissionsGroup class array to manage feature based permission list
    // features list whould be based on configured in the current namespace's products
    productPermissionGroups(permissionGroups: string[], searchKey: string = '') {
        const productFeatures = uniqBy(
            prop('name'),
            this.productEnabledFeatures(permissionGroups)
        )
        const featurePermissions = map(
            (feature: any) => new FeaturePermissionsGroup(feature, this.globalPermissions),
            productFeatures
        )

        return returnIsNotEmptyOrTrue(
            searchKey,
            filter((feature: FeaturePermissionsGroup) => {
                return feature.hasKey(searchKey)
            }, featurePermissions),
            featurePermissions
        )
    }
    // Merge all feautres permissions into single array of permissions
    productPermissions(permissionGroups: string[]) {
        const features = this.productPermissionGroups(permissionGroups)
        return flatten(map(f => f.featurePermissions(), features))
    }

    getFeatureVarMapName() {
        const featureVarMapName = `${getNamespaceName()}_site_configured_feature_names`
        return featureVarMapName
    }

}


export {
    Permission,
    ManageRolePermissions,
    RolePermissionClass,
    FeaturePermissionsGroup
}